import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  cancelAppointment,
  fetchAppointmentSuggestions,
  fetchAppointments,
  respondToAppointmentSchedule,
  respondToStaffProposal,
  uploadAppointmentPaymentSlip,
} from '../../api/appointments';
import ClientPaymentSlipUpload from '../../components/payments/ClientPaymentSlipUpload';
import { getPaymentPolicy, slipAllowsUpload } from '../../utils/paymentSlip';
import { submitReview, updateReview } from '../../api/reviews';
import AppointmentDetailDrawer from '../../components/appointments/AppointmentDetailDrawer';
import CancelAppointmentModal from '../../components/appointments/CancelAppointmentModal';
import StatusBadge from '../../components/appointments/StatusBadge';
import ClientReviewSummary from '../../components/reviews/ClientReviewSummary';
import ReviewModal from '../../components/reviews/ReviewModal';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import InvoiceActions from '../../components/invoices/InvoiceActions';
import { AppointmentPricingReceipt } from '../../components/pricing/AppointmentPriceAdjustment';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { useToast } from '../../context/ToastContext';

const ClientAppointmentsPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('upcoming');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['client-appointment-suggestions', tab],
    fetchSuggestions: (q) => fetchAppointmentSuggestions({ q, tab }),
  });
  const [chatId, setChatId] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [editReview, setEditReview] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['client-appointments', tab, page, pageSize, appliedSearch],
    queryFn: () =>
      fetchAppointments({
        tab,
        page,
        limit: pageSize,
        search: appliedSearch || undefined,
      }),
    staleTime: 30 * 1000,
  });

  const reviewMut = useMutation({
    mutationFn: (body) => submitReview(body),
    onSuccess: () => {
      showToast(t('toast.reviewThankYou'), 'success');
      setReviewTarget(null);
      queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.reviewSubmitFailed'), 'error'),
  });

  const updateReviewMut = useMutation({
    mutationFn: ({ id, body }) => updateReview(id, body),
    onSuccess: () => {
      showToast(t('toast.reviewUpdated'), 'success');
      setEditReview(null);
      queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }) => cancelAppointment(id, reason),
    onSuccess: () => {
      showToast(t('toast.appointmentCancelled'), 'success');
      setCancelTarget(null);
      queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
      setChatId(null);
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.cancelAppointmentFailed'), 'error'),
  });

  const scheduleRespMut = useMutation({
    mutationFn: ({ id, body }) => respondToAppointmentSchedule(id, body),
    onSuccess: (_, { body }) => {
      showToast(
        body.action === 'accept' ? t('toast.scheduleAccepted') : t('toast.scheduleRejected'),
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointment'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const [uploadingId, setUploadingId] = useState(null);

  const slipUploadMut = useMutation({
    mutationFn: ({ id, file, bankAccountId }) =>
      uploadAppointmentPaymentSlip(id, file, bankAccountId),
    onSuccess: () => {
      showToast(t('toast.paymentSlipUploaded'), 'success');
      setUploadingId(null);
      queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
    },
    onError: (err) => {
      setUploadingId(null);
      showToast(err.response?.data?.message || t('errors.uploadFailed'), 'error');
    },
  });

  const staffProposalRespMut = useMutation({
    mutationFn: ({ id, body }) => respondToStaffProposal(id, body),
    onSuccess: (_, { body }) => {
      showToast(
        body.action === 'accept' ? t('toast.staffProposalAccepted') : t('toast.staffProposalRejected'),
        'success'
      );
      queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
      queryClient.invalidateQueries({ queryKey: ['appointment'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const appointments = data?.appointments || [];
  const pagination = data?.pagination;

  const tabs = [
    { id: 'upcoming', label: t('appointments.tabs.upcoming') },
    { id: 'completed', label: t('appointments.tabs.completed') },
    { id: 'cancelled', label: t('appointments.tabs.cancelled') },
  ];

  return (
    <DashboardShell title={t('appointments.myAppointments')} subtitle={t('appointments.myAppointmentsSubtitle')}>
      <div className="mb-6 flex gap-2">
        {tabs.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            onClick={() => { setTab(tabItem.id); setPage(1); }}
            className={`rounded-lg px-4 py-2 text-sm ${tab === tabItem.id ? 'bg-glow-600/20 text-glow-300' : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
          >
            {tabItem.label}
          </button>
        ))}
        <Link to="/book" className="ml-auto rounded-lg bg-glow-600 px-4 py-2 text-sm text-white">
          {t('client.appointments.bookNew')}
        </Link>
      </div>

      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={t('appointments.client.searchPlaceholder')}
        showFiltersToggle={false}
      />

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      <div className="space-y-4">
        {appointments.map((a) => (
          <div key={a._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">{a.serviceName}</p>
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{a.branchName}</p>
                <p className="mt-2 text-sm">
                  {a.confirmedDate
                    ? `${new Date(a.confirmedDate).toLocaleDateString()} at ${a.confirmedTime}`
                    : `${new Date(a.preferredDate).toLocaleDateString()} at ${a.preferredTime} · ${t('appointments.awaitingConfirmation')}`}
                </p>
                {a.status === 'COMPLETED' && (
                  <div className="mt-2 space-y-3">
                    <AppointmentPricingReceipt appointment={a} compact />
                    <InvoiceActions kind="appointment" entityId={a._id} />
                  </div>
                )}
              </div>
              <StatusBadge status={a.status} />
            </div>

            {a.status === 'COMPLETED' && !a.review && (
              <div className="mt-4 rounded-lg border border-amber-800/40 bg-amber-950/20 p-4">
                <p className="text-sm text-amber-100/90">
                  {t('client.appointments.reviewPrompt', { service: a.serviceName, salon: a.salonName })}
                </p>
                <button
                  type="button"
                  onClick={() => setReviewTarget(a)}
                  className="mt-3 rounded-lg bg-amber-600/80 px-4 py-2 text-sm text-white"
                >
                  {t('client.history.leaveReview')}
                </button>
              </div>
            )}

            {a.review && (
              <ClientReviewSummary
                review={a.review}
                onEdit={() => setEditReview({ ...a.review, serviceName: a.serviceName, salonName: a.salonName })}
              />
            )}

            {a.staffProposalStatus === 'PENDING' && a.proposedStaffName && (
              <div className="mt-4 space-y-2 rounded-lg border border-amber-800/40 bg-amber-950/20 p-4">
                <p className="text-sm text-amber-100/90">
                  {t('appointments.drawer.staffProposalPrompt', {
                    proposed: a.proposedStaffName,
                    preferred: a.preferredStaffName || t('appointments.drawer.yourChoice'),
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={staffProposalRespMut.isPending}
                    onClick={() => staffProposalRespMut.mutate({ id: a._id, body: { action: 'accept' } })}
                    className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {t('appointments.drawer.acceptStaffChange')}
                  </button>
                  <button
                    type="button"
                    disabled={staffProposalRespMut.isPending}
                    onClick={() =>
                      staffProposalRespMut.mutate({
                        id: a._id,
                        body: { action: 'reject', reason: t('appointments.drawer.staffChangeNotOk') },
                      })
                    }
                    className="flex-1 rounded-lg border border-red-800 py-2 text-sm text-red-400 disabled:opacity-50"
                  >
                    {t('appointments.drawer.rejectStaffChange')}
                  </button>
                </div>
              </div>
            )}

            {a.status === 'APPROVED' && a.scheduleStatus === 'PENDING' && a.confirmedDate && a.confirmedTime && (
              <div className="mt-4 space-y-2 rounded-lg border border-amber-800/40 bg-amber-950/20 p-4">
                <p className="text-sm text-amber-100/90">{t('appointments.drawer.scheduleConfirmPrompt')}</p>
                <p className="text-xs text-zinc-500">
                  {new Date(a.confirmedDate).toLocaleDateString()} · {a.confirmedTime}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={scheduleRespMut.isPending}
                    onClick={() => scheduleRespMut.mutate({ id: a._id, body: { action: 'accept' } })}
                    className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm text-white disabled:opacity-50"
                  >
                    {t('appointments.drawer.acceptSchedule')}
                  </button>
                  <button
                    type="button"
                    disabled={scheduleRespMut.isPending}
                    onClick={() =>
                      scheduleRespMut.mutate({
                        id: a._id,
                        body: { action: 'reject', reason: t('appointments.drawer.scheduleNotOk') },
                      })
                    }
                    className="flex-1 rounded-lg border border-red-800 py-2 text-sm text-red-400 disabled:opacity-50"
                  >
                    {t('appointments.drawer.rejectSchedule')}
                  </button>
                </div>
              </div>
            )}

            {a.status === 'CANCELLED' && a.cancellationReason && (
              <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-500">
                <span className="text-zinc-600 dark:text-zinc-400">{t('client.appointments.cancellationReasonLabel')}</span>
                {a.cancellationReason}
              </p>
            )}

            {slipAllowsUpload(getPaymentPolicy(a)) && a.status !== 'CANCELLED' && (
              <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-800 p-3">
                <ClientPaymentSlipUpload
                  bankAccounts={a.bankAccounts || []}
                  uploading={uploadingId === a._id && slipUploadMut.isPending}
                  onUpload={(file, bankAccountId) => {
                    setUploadingId(a._id);
                    slipUploadMut.mutate({ id: a._id, file, bankAccountId });
                  }}
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setChatId(a._id)}
                className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                {t('client.appointments.openChat')}
              </button>
              {a.status === 'PENDING' && (
                <button
                  type="button"
                  disabled={cancelMut.isPending}
                  onClick={() => setCancelTarget(a)}
                  className="rounded-lg border border-red-800/60 px-3 py-1.5 text-xs text-red-400 hover:bg-red-950/30 disabled:opacity-50"
                >
                  {t('client.appointments.cancelRequest')}
                </button>
              )}
            </div>
          </div>
        ))}
        {!isLoading && !appointments.length && (
          <p className="text-center text-zinc-600 dark:text-zinc-500">{t('client.appointments.noTabItems')}</p>
        )}
      </div>

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <AppointmentDetailDrawer
        appointmentId={chatId}
        onClose={() => setChatId(null)}
        clientMode
      />

      <CancelAppointmentModal
        open={!!cancelTarget}
        appointment={cancelTarget}
        onClose={() => setCancelTarget(null)}
        saving={cancelMut.isPending}
        onSubmit={(reason) => cancelMut.mutate({ id: cancelTarget._id, reason })}
      />

      <ReviewModal
        open={!!reviewTarget}
        title={t('reviews.reviewService')}
        subtitle={
          reviewTarget
            ? t('reviews.serviceAtSalon', {
                service: reviewTarget.serviceName,
                salon: reviewTarget.salonName,
              })
            : ''
        }
        onClose={() => setReviewTarget(null)}
        saving={reviewMut.isPending}
        onSubmit={({ rating, comment }) =>
          reviewMut.mutate({
            type: 'SERVICE',
            tenantId: reviewTarget.tenantId?._id || reviewTarget.tenantId,
            branchId: reviewTarget.branchId?._id || reviewTarget.branchId,
            appointmentId: reviewTarget._id,
            rating,
            comment,
          })
        }
      />

      <ReviewModal
        open={!!editReview}
        title={t('reviews.editReview')}
        subtitle={
          editReview
            ? t('reviews.serviceAtSalon', {
                service: editReview.serviceName,
                salon: editReview.salonName,
              })
            : ''
        }
        initialRating={editReview?.rating || 0}
        initialComment={editReview?.comment || ''}
        submitLabel={t('common.save')}
        onClose={() => setEditReview(null)}
        saving={updateReviewMut.isPending}
        onSubmit={({ rating, comment }) =>
          updateReviewMut.mutate({ id: editReview._id, body: { rating, comment } })
        }
      />
    </DashboardShell>
  );
};

export default ClientAppointmentsPage;
